import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { startPingService } from '../utils/pingService';
import { toast } from '../components/Toast';

const GameContext = createContext();

export const useGame = () => useContext(GameContext);

const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const readStoredGame = () => {
  try {
    const raw = sessionStorage.getItem('currentGame');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.roomId === 'string' ? { roomId: parsed.roomId, status: parsed.status || null } : null;
  } catch (_) {
    return null;
  }
};

export const GameProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [currentGame, setCurrentGameState] = useState(readStoredGame);
  const [publicGames, setPublicGames] = useState([]);
  const { token, isGuest, guestId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  // useNavigate() is not stable across route changes, so the socket effect reads it through a ref
  // instead of depending on it (otherwise every navigation would reconnect the socket).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // currentGame is { roomId, status } for the room this player is in, or null.
  const setCurrentGame = useCallback((game) => {
    const next = game && game.roomId ? { roomId: game.roomId, status: game.status || null } : null;
    setCurrentGameState(next);
    try {
      if (next) sessionStorage.setItem('currentGame', JSON.stringify(next));
      else sessionStorage.removeItem('currentGame');
    } catch (_) { /* storage unavailable */ }
  }, []);

  const clearCurrentGame = useCallback(() => setCurrentGame(null), [setCurrentGame]);

  useEffect(() => {
    const credentials = token ? { token } : (isGuest && guestId ? { isGuest: true, guestId } : null);
    if (!credentials) return undefined;

    const s = io(SERVER_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      path: '/socket.io/',
    });

    // 'connect' fires on every reconnect too, so authentication is always redone. Pages that hold a
    // room re-join it when `authed` turns true again.
    s.on('connect', () => {
      setConnected(true);
      s.emit('authenticate', credentials);
    });
    s.on('disconnect', () => { setConnected(false); setAuthed(false); });
    s.on('connect_error', () => setConnected(false));
    s.on('authenticated', () => {
      setAuthed(true);
      s.emit('get_public_games');
    });
    s.on('auth_error', (data) => {
      setAuthed(false);
      toast((data && data.message) || 'Could not sign in to the game server', { error: true });
    });
    const onRoom = (data) => {
      if (!data || !data.roomId) return;
      setCurrentGame({ roomId: data.roomId, status: data.game && data.game.status });
      if (pathRef.current !== `/game/${data.roomId}`) navigateRef.current(`/game/${data.roomId}`);
    };
    s.on('room_created', onRoom);
    s.on('room_joined', onRoom);
    s.on('reconnected_to_game', (data) => {
      if (data && data.roomId) setCurrentGame({ roomId: data.roomId, status: data.game && data.game.status });
    });
    s.on('game_error', (data) => {
      const message = (data && data.message) || 'Something went wrong';
      if (data && data.code === 'ALREADY_IN_GAME' && data.roomId) setCurrentGame({ roomId: data.roomId });
      toast(message, { error: true });
    });
    s.on('public_games', (games) => setPublicGames(Array.isArray(games) ? games : []));

    setSocket(s);
    return () => {
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setConnected(false);
      setAuthed(false);
    };
  }, [token, isGuest, guestId, setCurrentGame]);

  // Keep the free-tier backend awake.
  useEffect(() => startPingService(), []);

  const ready = useCallback(() => {
    if (!socket || !connected || !authed) {
      toast('Not connected to the game server yet. Try again in a moment.', { error: true });
      return false;
    }
    return true;
  }, [socket, connected, authed]);

  const checkActiveGame = useCallback(() => currentGame !== null, [currentGame]);

  const returnToActiveGame = useCallback(() => {
    if (currentGame && currentGame.roomId) {
      navigate(`/game/${currentGame.roomId}`);
      return true;
    }
    return false;
  }, [currentGame, navigate]);

  const createGame = useCallback((options = {}) => {
    if (!ready()) return;
    socket.emit('create_room', { isPublic: options.isPublic !== false, isRated: !!options.isRated });
  }, [ready, socket]);

  // Resolves true once the room is joined (the context navigates there), false on error or timeout.
  const joinGame = useCallback((roomId) => {
    const code = String(roomId || '').trim().toUpperCase();
    if (!code) return Promise.resolve(false);
    if (!ready()) return Promise.resolve(false);
    return new Promise((resolve) => {
      let timer = null;
      const done = (ok) => {
        clearTimeout(timer);
        socket.off('room_joined', onJoined);
        socket.off('game_error', onError);
        resolve(ok);
      };
      const onJoined = (data) => { if (data && data.roomId === code) done(true); };
      const onError = () => done(false);
      socket.on('room_joined', onJoined);
      socket.on('game_error', onError);
      timer = setTimeout(() => done(false), 8000);
      socket.emit('join_room', { roomId: code });
    });
  }, [ready, socket]);

  const leaveGame = useCallback((roomId) => {
    const id = roomId || (currentGame && currentGame.roomId);
    if (socket && id) socket.emit('leave_room', { roomId: id });
    setCurrentGame(null);
    return Promise.resolve(true);
  }, [socket, currentGame, setCurrentGame]);

  const playAgain = useCallback((roomId) => {
    if (!ready()) return;
    socket.emit('play_again', { roomId });
  }, [ready, socket]);

  const fetchPublicGames = useCallback(() => {
    if (socket && connected) socket.emit('get_public_games');
  }, [socket, connected]);

  const value = {
    socket,
    connected,
    authed,
    currentGame,
    publicGames,
    createGame,
    joinGame,
    leaveGame,
    playAgain,
    fetchPublicGames,
    checkActiveGame,
    returnToActiveGame,
    setCurrentGame,
    clearCurrentGame,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
